import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Upload, Button, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { getSignedUrl } from 'store/commonSlice';
import { API } from 'api';

const { Dragger } = Upload;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
	}
	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 0;
	}
	.ant-modal-body {
		padding: 24px;
	}
	.ant-modal-footer {
		border-top: none;
		padding: 0 24px 24px;
	}
`;

const ModalTitle = styled.h2`
	margin: 0;
	font-size: 24px;
	font-weight: 600;
	color: #000;
	text-align: center;
`;

const StyledDragger = styled(Dragger)`
	.ant-upload-drag {
		border: 2px dashed #d9d9d9;
		border-radius: 8px;
		background: #fafafa;
		padding: 60px 20px;
		transition: all 0.3s;

		&:hover {
			border-color: #143e60;
		}
	}

	.ant-upload-drag-icon {
		margin-bottom: 16px;
		.anticon {
			font-size: 48px;
			color: #143e60;
		}
	}

	.ant-upload-text {
		font-size: 16px;
		color: #000;
		margin-bottom: 8px;
	}

	.ant-upload-hint {
		font-size: 14px;
		color: #8c8c8c;
	}
`;

const ButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	min-width: 120px;
	height: 40px;
	border: 1px solid #143e60;
	color: #143e60;
	background: #ffffff;
	font-size: 16px;
	border-radius: 4px;

	&:hover {
		border-color: #1a4d7a;
		color: #1a4d7a;
		background: #ffffff;
	}
`;

const SubmitButton = styled(Button)`
	min-width: 120px;
	height: 40px;
	background: #143e60;
	color: #ffffff;
	border: none;
	font-size: 16px;
	border-radius: 4px;

	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}

	&:disabled {
		background: #d9d9d9;
		color: #8c8c8c;
	}
`;

function FileUploadModal({
	visible,
	onClose,
	accept = ['.xlsx', '.xls', '.csv'],
	uploadType = 'finance_utr',
	entity = 'finance_utr_bulk_upload',
	onUploadSuccess,
}) {
	const dispatch = useDispatch();
	const [fileList, setFileList] = useState([]);
	const [uploading, setUploading] = useState(false);

	const handleClose = () => {
		setFileList([]);
		onClose();
	};
	// eslint-disable-next-line sonarjs/cognitive-complexity
	const handleSubmit = async () => {
		if (fileList.length === 0) {
			message.error('Please select a file to upload');
			return;
		}

		setUploading(true);
		const file = fileList[0];

		try {
			// Step 1: Get signed URL from backend
			const fileExtension = file.name.split('.').pop().toLowerCase();
			const contentType =
				fileExtension === 'csv'
					? 'text/csv'
					: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

			const signedUrlData = {
				url_type: 'upload',
				data: {
					entity,
					keys: [file.name],
					content_type: contentType,
				},
			};

			message.loading({ content: 'Getting upload URL...', key: 'upload' });
			const signedUrlResponse = await dispatch(getSignedUrl(signedUrlData));

			if (!signedUrlResponse?.data?.[0]?.url || !signedUrlResponse?.data?.[0]?.key) {
				throw new Error('Failed to get upload URL');
			}

			const uploadUrl = signedUrlResponse.data[0].url;
			const s3Key = signedUrlResponse.data[0].filename;

			// Step 2: Upload file to S3 using signed URL
			message.loading({ content: 'Uploading file to S3...', key: 'upload' });
			const uploadResponse = await fetch(uploadUrl, {
				method: 'put',
				body: file,
				headers: {
					'Content-Type': contentType,
				},
			});

			if (!uploadResponse.ok) {
				throw new Error('Failed to upload file to S3');
			}

			// Step 3: Trigger backend processing of the uploaded file
			message.loading({ content: 'Processing file...', key: 'upload' });

			const processResponse = await API.common({
				method: 'post',
				url: '/bulk-upload',
				data: {
					upload_type: uploadType,
					data: {
						key: s3Key,
					},
				},
			});

			// Check if processing was successful
			if (processResponse?.status === 'success') {
				const { success_row, error_row, url } = processResponse.data;

				message.success({
					content: `Processing complete! Success: ${success_row}, Errors: ${error_row}`,
					key: 'upload',
					duration: 5,
				});

				// If there's a download URL for the results, optionally download it
				if (url && error_row > 0) {
					message.info({
						content: 'Downloaded the results file to see error details',
						duration: 5,
					});
					window.open(url, '_blank');
				}

				// Call success callback if provided
				if (onUploadSuccess) {
					onUploadSuccess(processResponse);
				}
			} else {
				message.success({
					content: 'File processed successfully',
					key: 'upload',
					duration: 3,
				});

				if (onUploadSuccess) {
					onUploadSuccess(processResponse);
				}
			}

			handleClose();
		} catch (error) {
			console.error('Upload error:', error);
			message.error({
				content: error.message || 'Failed to process file',
				key: 'upload',
				duration: 5,
			});
		} finally {
			setUploading(false);
		}
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		fileList,
		accept: accept.join(','),
		beforeUpload: (file) => {
			// Validate file type
			const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
			if (!accept.includes(fileExtension)) {
				message.error(`Only ${accept.join(', ')} files are allowed`);
				return Upload.LIST_IGNORE;
			}

			// Validate file size (max 10MB)
			const isLt10M = file.size / 1024 / 1024 < 10;
			if (!isLt10M) {
				message.error('File must be smaller than 10MB');
				return Upload.LIST_IGNORE;
			}

			setFileList([file]);
			return false; // Prevent auto upload
		},
		onRemove: () => {
			setFileList([]);
		},
	};

	return (
		<StyledModal
			title={<ModalTitle>Upload File</ModalTitle>}
			open={visible}
			onCancel={handleClose}
			footer={null}
			width={600}
			centered
			destroyOnClose
		>
			<StyledDragger {...uploadProps}>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">
					Drag & drop files or <span style={{ color: '#143e60' }}>Browse</span>
				</p>
			</StyledDragger>

			<ButtonContainer>
				<CancelButton onClick={handleClose} disabled={uploading}>
					Cancel
				</CancelButton>
				<SubmitButton
					onClick={handleSubmit}
					loading={uploading}
					disabled={fileList.length === 0 || uploading}
				>
					Submit
				</SubmitButton>
			</ButtonContainer>
		</StyledModal>
	);
}

FileUploadModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	accept: PropTypes.array,
	uploadType: PropTypes.string,
	entity: PropTypes.string,
	onUploadSuccess: PropTypes.func,
};

export default FileUploadModal;
